import React, { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, GridLegacy as Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { IPreferences } from "../../../logic/api";
import NumberSpinnerInputField from "../../../components/editable/EditableField/fields/NumberSpinnerInputField";

interface KmosSettingsDialogProps {
    open: boolean;
    onClose: () => void;
    preferences: IPreferences;
    onSave: (preferences: IPreferences) => void;
}

export const KmosSettingsDialog: React.FC<KmosSettingsDialogProps> = ({
    open,
    onClose,
    preferences,
    onSave,
}) => {
    const { t } = useTranslation();
    const [daysToDisplay, setDaysToDisplay] = useState(preferences.kmos_days_to_display || 0);
    const [dueInHours, setDueInHours] = useState(preferences.kmos_duein_hours_ahead || 1);
    const [hasChanges, setHasChanges] = useState(false);

    useEffect(() => {
        const changed =
            daysToDisplay !== preferences.kmos_days_to_display ||
            dueInHours !== preferences.kmos_duein_hours_ahead;
        setHasChanges(changed);
    }, [daysToDisplay, dueInHours, preferences]);

    const handleSave = () => {
        const updatedPreferences = {
            ...preferences,
            kmos_days_to_display: daysToDisplay,
            kmos_duein_hours_ahead: Math.max(0, dueInHours),
        };
        onSave(updatedPreferences);
        onClose();
    };

    const handleCancel = () => {
        setDaysToDisplay(preferences.kmos_days_to_display);
        setDueInHours(preferences.kmos_duein_hours_ahead);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleCancel}>
            <DialogTitle>
                <div>{t("kmos.labels.keyMasterSettings")}</div>
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <NumberSpinnerInputField
                            label={t("settings.displayDays")}
                            value={daysToDisplay}
                            onChange={setDaysToDisplay}
                            min={0}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <NumberSpinnerInputField
                            label={t("settings.dueInHoursAhead")}
                            value={dueInHours}
                            onChange={setDueInHours}
                            min={1}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancel}>{t("general.cancel")}</Button>
                <Button
                    onClick={handleSave}
                    color="primary"
                    disabled={!hasChanges}
                >
                    {t("general.save")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};